<template>
	<App title="Logs">
        <div class="py-4">
            <div class="container">
                <div class="row align-items-center">
                    <div class="col">
                        <h2 class="mb-0">Activity Log</h2>
                        <small class="text-muted">Recent system activities and events</small>
                    </div>
                    <div class="col-auto">
                        <button class="btn btn-sm btn-outline-primary" @click="refreshActivities" :disabled="loading">
                            <i class="bi bi-arrow-clockwise me-1" :class="{ 'animate-spin': loading }"></i>
                            {{ loading ? 'Loading...' : 'Refresh' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="container">
            <!-- Filters -->
            <div class="row mb-4">
                <div class="col">
                    <div class="d-flex gap-2 flex-wrap">
                        <button 
                            class="filter-btn" 
                            :class="{ active: activeFilter === 'all' }"
                            @click="setFilter('all')"
                        >
                            All
                        </button>
                        <button 
                            class="filter-btn" 
                            :class="{ active: activeFilter === 'created' }"
                            @click="setFilter('created')"
                        >
                            Created
                        </button>
                        <button 
                            class="filter-btn" 
                            :class="{ active: activeFilter === 'updated' }"
                            @click="setFilter('updated')"
                        >
                            Updated
                        </button>
                        <button 
                            class="filter-btn" 
                            :class="{ active: activeFilter === 'deleted' }"
                            @click="setFilter('deleted')"
                        >
                            Deleted
                        </button>
                    </div>
                </div>
            </div>

            <!-- Activity Groups -->
            <div v-if="loading && activities.length === 0" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>

            <div v-else-if="Object.keys(filteredActivities).length === 0" class="text-center py-5">
                <div class="text-muted">
                    <i class="bi bi-inbox fs-1 d-block mb-3"></i>
                    <h5>No activities found</h5>
                    <p>{{ activeFilter === 'all' ? 'No activities available' : `No ${activeFilter} activities found` }}</p>
                </div>
            </div>

            <div v-else>
                <div v-for="(dayActivities, date) in filteredActivities" :key="date" class="mb-4">
                    <!-- Date Header -->
                    <div class="mb-3">
                        <h5 class="fw-semibold text-primary mb-0">{{ date }}</h5>
                        <hr class="mt-2 mb-3">
                    </div>

                    <!-- Activity Items for this date -->
                    <div class="row g-3">
                        <div v-for="activity in dayActivities" :key="activity.id" class="col-12">
                            <div class="card activity-card">
                                <div class="card-body p-3">
                                    <div class="d-flex align-items-start gap-3">
                                        <div 
                                            class="activity-icon"
                                            :class="getActivityIconClass(activity.action)"
                                        >
                                            <i :class="getActivityIcon(activity.action)"></i>
                                        </div>
                                        <div class="flex-grow-1">
                                           
                                            <div class="activity-desc">{{ activity.description }}</div>
                                            <!--
                                            <span class="text-xs text-black-50 text-start">{{ formatActionTitle(activity.action) }}</span>
                                            -->
                                        </div>
                                        <div class="activity-time">{{ formatTime(activity.created_at) }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Load More -->
            <div v-if="hasMoreData" class="text-center mt-4 mb-5">
                <button 
                    class="btn btn-outline-primary" 
                    @click="loadMoreActivities"
                    :disabled="loadingMore"
                >
                    {{ loadingMore ? 'Loading...' : 'Load More Activities' }}
                </button>
            </div>
        </div>
	</App>
</template>

<script lang="ts">
import App from '@/vue/templates/App.vue'
import { AxiosError } from 'axios'
import { Component, Vue, toNative } from 'vue-facing-decorator'

interface ActivityItem {
    id: number;
    action: string;
    description: string;
    created_at: string;
}

interface ActivityData {
    [date: string]: ActivityItem[];
}

@Component({
	components: {
		App,
	},
})
class Activity extends Vue {
    activities: ActivityData = {}
    loading = false
    loadingMore = false
    activeFilter = 'all'
    currentPage = 1
    hasMoreData = true

    mounted() {
        this.loadActivities()
    }

    get filteredActivities(): ActivityData {
        if (this.activeFilter === 'all') {
            return this.activities
        }

        const filtered: ActivityData = {}
        
        Object.keys(this.activities).forEach(date => {
            const filteredItems = this.activities[date].filter(
                activity => activity.action === this.activeFilter
            )
            
            if (filteredItems.length > 0) {
                filtered[date] = filteredItems
            }
        })
        
        return filtered
    }

    async loadActivities() {
        this.loading = true
        try {
            const response = await this.$http.get(this.route('activity.ajax.log'), {
                params: { 
                    page: 1,
                    per_page: 10
                }
            })
            
            if (response.data && response.data.data) {
                this.activities = response.data.data
                this.currentPage = 1
                // Use pagination info if available, otherwise fallback to item count check
                if (response.data.pagination) {
                    this.hasMoreData = response.data.pagination.has_more_pages
                } else {
                    const totalItems = Object.values(response.data.data).flat().length
                    this.hasMoreData = totalItems >= 20
                }
            }
        } catch (e) {
            console.error('An error occurred:', e)
            this.activities = {}
            this.hasMoreData = false
        } finally {
            this.loading = false
        }
    }

    async loadMoreActivities() {
        if (this.loadingMore || !this.hasMoreData) return
        
        this.loadingMore = true
        try {
            const nextPage = this.currentPage + 1
            const response = await this.$http.get(this.route('activity.ajax.log'), {
                params: { 
                    page: nextPage,
                    per_page: 20
                }
            })
            
            if (response.data && response.data.data) {
                const newData = response.data.data
                const newItemsCount = Object.values(newData).flat().length
                
                if (newItemsCount === 0) {
                    this.hasMoreData = false
                } else {
                    // Merge new data with existing data
                    Object.keys(newData).forEach(date => {
                        if (this.activities[date]) {
                            this.activities[date] = [...this.activities[date], ...newData[date]]
                        } else {
                            this.activities[date] = newData[date]
                        }
                    })
                    
                    this.currentPage = nextPage
                    
                    // Use pagination info if available, otherwise fallback to item count check
                    if (response.data.pagination) {
                        this.hasMoreData = response.data.pagination.has_more_pages
                    } else {
                        this.hasMoreData = newItemsCount >= 20
                    }
                }
            } else {
                this.hasMoreData = false
            }
        } catch (e) {
            console.error('Error loading more activities:', e)
            this.hasMoreData = false
        } finally {
            this.loadingMore = false
        }
    }

    async refreshActivities() {
        await this.loadActivities()
    }

    setFilter(filter: string) {
        this.activeFilter = filter
    }

    getActivityIconClass(action: string): string {
        switch (action.toLowerCase()) {
            case 'created':
                return 'bg-success bg-opacity-10 text-success'
            case 'updated':
                return 'bg-primary bg-opacity-10 text-primary'
            case 'deleted':
                return 'bg-danger bg-opacity-10 text-danger'
            default:
                return 'bg-secondary bg-opacity-10 text-secondary'
        }
    }

    getActivityIcon(action: string): string {
        switch (action.toLowerCase()) {
            case 'created':
                return 'bi bi-plus-circle'
            case 'updated':
                return 'bi bi-pencil-square'
            case 'deleted':
                return 'bi bi-trash'
            default:
                return 'bi bi-info-circle'
        }
    }

    formatActionTitle(action: string): string {
        return action.charAt(0).toUpperCase() + action.slice(1)
    }

    formatTime(dateString: string): string {
        const date = new Date(dateString)
        const now = new Date()
        const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
        
        if (diffInMinutes < 1) {
            return 'Just now'
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes} min${diffInMinutes !== 1 ? 's' : ''} ago`
        } else if (diffInMinutes < 1440) {
            const hours = Math.floor(diffInMinutes / 60)
            return `${hours} hour${hours !== 1 ? 's' : ''} ago`
        } else {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        }
    }
}

export default toNative(Activity)
</script>

<style scoped>
body {
    background-color: #f8f9fa;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.activity-card {
    border: none;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    transition: all 0.2s ease;
}

.activity-card:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.activity-icon {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
}

.activity-time {
    font-size: 0.85rem;
    color: #6c757d;
    white-space: nowrap;
}

.activity-title {
    font-weight: 500;
    margin-bottom: 0.25rem;
}

.activity-desc {
    font-size: 0.9rem;
    color: #6c757d;
    margin-bottom: 0;
    line-height: 1.4;
}

.filter-btn {
    border-radius: 20px;
    padding: 0.375rem 1rem;
    font-size: 0.875rem;
    border: 1px solid #dee2e6;
    background: white;
    color: #6c757d;
    transition: all 0.2s ease;
    cursor: pointer;
}

.filter-btn.active {
    background: #0d6efd;
    color: white;
    border-color: #0d6efd;
}

.filter-btn:hover:not(.active) {
    border-color: #0d6efd;
    color: #0d6efd;
}

.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>